'use client';

import type React from 'react';
import { useState, useRef, useEffect } from 'react';
import Image from 'next/image';

import { Button } from '@simulmedia/ui-kit';
import { cn } from '@skybeam/shared-utils';
import { SignupLinkWithUTM } from '../sign-up-link-with-UTM';

export interface TabContent {
  content: React.ReactNode;
  heading: string;
  imageUrl: string;
  label: string;
  laptopImageUrl: string;
}

interface VerticalTabsProps {
  data: TabContent[];
  sectionRef: React.RefObject<HTMLDivElement | null>;
  trackRef: React.RefObject<HTMLDivElement | null>;
}

const DESKTOP_BREAKPOINT = 1440;
const LAPTOP_BREAKPOINT = 960;
const STICKY_TOP = 80;

function useScrollProgressTabSwitch({
  data,
  onChangeTab,
  sectionRef,
  trackRef,
}: {
  data: TabContent[];
  onChangeTab: (index: number) => void;
  sectionRef: React.RefObject<HTMLDivElement | null>;
  trackRef: React.RefObject<HTMLDivElement | null>;
}): void {
  const rafIdRef = useRef<number | null>(null);
  const lastIndexRef = useRef<number>(-1);
  const sectionPageTopRef = useRef<number>(0);
  const stickyHeightPxRef = useRef<number>(0);

  const updateMeasurements = () => {
    if (typeof window === 'undefined') return;

    const isDesktop = window.innerWidth >= DESKTOP_BREAKPOINT;
    if (!isDesktop) return;

    const viewportHeight = window.innerHeight;
    const stickyHeightPx = viewportHeight - STICKY_TOP;

    if (sectionRef.current) {
      const sectionRect = sectionRef.current.getBoundingClientRect();
      const scrollY = window.scrollY;
      sectionPageTopRef.current = sectionRect.top + scrollY;
    }

    const tabCount = data.length;
    const totalPinnedScroll = (tabCount - 1) * viewportHeight;
    const trackHeightPx = stickyHeightPx + totalPinnedScroll;

    if (trackRef.current) {
      trackRef.current.style.height = `${trackHeightPx}px`;
    }

    stickyHeightPxRef.current = stickyHeightPx;
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const isDesktop = window.innerWidth >= DESKTOP_BREAKPOINT;
    if (!isDesktop || !sectionRef.current || !trackRef.current) return;

    updateMeasurements();

    const updateActiveTab = () => {
      const isDesktop = window.innerWidth >= DESKTOP_BREAKPOINT;
      if (!isDesktop || !sectionRef.current || !trackRef.current) {
        return;
      }

      const scrollY = window.scrollY;
      const viewportHeight = window.innerHeight;
      const sectionPageTop = sectionPageTopRef.current;
      const tabCount = data.length;

      const startY = sectionPageTop - STICKY_TOP;

      const totalPinnedScroll = (tabCount - 1) * viewportHeight;

      if (totalPinnedScroll <= 0) {
        if (lastIndexRef.current !== 0) {
          lastIndexRef.current = 0;
          onChangeTab(0);
        }
        return;
      }

      const scrollDistance = scrollY - startY;
      const progress = Math.max(
        0,
        Math.min(1, scrollDistance / totalPinnedScroll),
      );

      const rawIndex = Math.floor(progress * tabCount);
      const activeIndex = Math.min(tabCount - 1, Math.max(0, rawIndex));

      if (activeIndex !== lastIndexRef.current) {
        lastIndexRef.current = activeIndex;
        onChangeTab(activeIndex);
      }
    };

    const handleScroll = () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
      }
      rafIdRef.current = requestAnimationFrame(updateActiveTab);
    };

    const handleResize = () => {
      updateMeasurements();
      handleScroll();
    };

    const resizeObserver = new ResizeObserver(() => {
      updateMeasurements();
      handleScroll();
    });

    if (sectionRef.current) {
      resizeObserver.observe(sectionRef.current);
    }
    if (trackRef.current) {
      resizeObserver.observe(trackRef.current);
    }

    window.addEventListener('load', updateMeasurements);

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('load', updateMeasurements);
      resizeObserver.disconnect();
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
      }
    };
  }, [data.length, onChangeTab, sectionRef, trackRef, updateMeasurements]);
}

interface MobileTabProps {
  tab: TabContent;
}

const MobileTab: React.FC<MobileTabProps> = ({ tab }) => {
  const [isLaptop, setIsLaptop] = useState(false);

  useEffect(() => {
    const checkIsLaptop = () => {
      setIsLaptop(window.innerWidth >= LAPTOP_BREAKPOINT);
    };

    checkIsLaptop();
    window.addEventListener('resize', checkIsLaptop);

    return () => {
      window.removeEventListener('resize', checkIsLaptop);
    };
  }, []);

  return (
    <div className="flex w-full flex-col gap-y-13">
      <div className="tablet:aspect-520/578 laptop:aspect-880/674 relative aspect-272/303 w-full">
        <Image
          src={isLaptop ? tab.laptopImageUrl : tab.imageUrl}
          alt={`${tab.label} illustration`}
          fill
          className="size-full object-contain"
        />
      </div>
      <div className="flex flex-col gap-y-7">
        <h3 className="heading-marketing-3 font-light">{tab.heading}</h3>
        <div className="text-base text-gray-600">{tab.content}</div>
        <SignupLinkWithUTM target="_blank">
          <Button className="rounded-lg px-6 text-base" variant="primary">
            Get Started
          </Button>
        </SignupLinkWithUTM>
      </div>
    </div>
  );
};

interface DesktopTabLabelProps {
  isActive: boolean;
  onClick: () => void;
  tab: TabContent;
}

const DesktopTabLabel: React.FC<DesktopTabLabelProps> = ({
  isActive,
  onClick,
  tab,
}) => (
  <div
    data-test="get-seen-tab-label"
    className={cn(
      'flex w-fit cursor-pointer items-center gap-x-3 text-base text-gray-300 hover:text-gray-600',
      isActive ? 'text-gray-600' : '',
    )}
    onClick={onClick}
  >
    <div
      data-test="get-seen-tab-label-indicator"
      className={cn(
        'size-2 shrink-0 rounded-full',
        isActive ? 'bg-blue-700' : 'bg-transparent',
      )}
    />
    {tab.label}
  </div>
);

interface DesktopTabContentProps {
  isActive: boolean;
  tab: TabContent;
}

const DesktopTabContent: React.FC<DesktopTabContentProps> = ({
  isActive,
  tab,
}) => (
  <div
    data-test="get-seen-tab-panel"
    className={cn(
      'absolute inset-0 flex flex-col gap-y-8.5 transition-opacity duration-500',
      isActive ? 'z-10 opacity-100' : 'z-0 opacity-0',
    )}
  >
    <h3
      className="heading-marketing-3 max-w-100 pt-1 leading-[1.13] font-light"
      data-test="get-seen-tab-title"
    >
      {tab.heading}
    </h3>
    <div
      className="max-w-[464px] text-base text-gray-600"
      data-test="get-seen-tab-content"
    >
      {tab.content}
    </div>
    <SignupLinkWithUTM
      className="mt-[18px]"
      data-test="get-seen-tab-button"
      target="_blank"
    >
      <Button
        className="rounded-lg px-6 text-[15px] font-semibold"
        variant="primary"
      >
        Get Started
      </Button>
    </SignupLinkWithUTM>
  </div>
);

interface DesktopTabImageProps {
  isActive: boolean;
  tab: TabContent;
}

const DesktopTabImage: React.FC<DesktopTabImageProps> = ({ isActive, tab }) => (
  <div
    className={cn(
      'absolute inset-0 flex flex-col gap-y-1.5 transition-opacity duration-500',
      isActive ? 'z-10 opacity-100' : 'z-0 opacity-0',
    )}
  >
    <div
      className="relative h-[624px] w-[560px]"
      data-test="get-seen-tab-image"
    >
      <Image
        src={tab.imageUrl}
        alt={`${tab.label} illustration`}
        fill
        className="object-contain"
      />
    </div>
    <div className="text-end text-[8px] font-light text-[#B0B0B0]">
      Data is not real and for illustrative purposes only
    </div>
  </div>
);

export const VerticalTabs: React.FC<VerticalTabsProps> = ({
  data,
  sectionRef,
  trackRef,
}) => {
  const [activeTab, setActiveTab] = useState(data[0].label);

  const handleTabChange = (index: number) => {
    const newTab = data[index];
    if (newTab) {
      setActiveTab(newTab.label);
    }
  };

  const handleTabClick = (clickedIndex: number) => {
    if (typeof window === 'undefined') return;

    const isDesktop = window.innerWidth >= DESKTOP_BREAKPOINT;
    if (!isDesktop || !sectionRef.current) {
      handleTabChange(clickedIndex);
      return;
    }

    const tabCount = data.length;
    if (tabCount <= 1) {
      return;
    }

    const sectionRect = sectionRef.current.getBoundingClientRect();
    const scrollY = window.scrollY;
    const viewportHeight = window.innerHeight;

    const sectionPageTop = sectionRect.top + scrollY;
    const startY = sectionPageTop - STICKY_TOP;

    const totalPinnedScroll = (tabCount - 1) * viewportHeight;

    const targetScrollY = startY + clickedIndex * viewportHeight;

    const clampedScrollY = Math.max(
      startY,
      Math.min(startY + totalPinnedScroll, targetScrollY),
    );

    window.scrollTo({
      top: clampedScrollY,
      behavior: 'smooth',
    });
  };

  useScrollProgressTabSwitch({
    data,
    onChangeTab: handleTabChange,
    sectionRef,
    trackRef,
  });

  return (
    <>
      <div className="desktop:hidden flex w-full flex-col gap-y-12">
        {data.map((tab) => (
          <MobileTab key={tab.label} tab={tab} />
        ))}
      </div>

      <div className="desktop:flex hidden w-full flex-row items-start gap-x-12">
        <div className="flex size-full w-1/2 flex-col justify-center gap-y-10.5">
          <div className="flex flex-col gap-y-2">
            {data.map((tab, index) => (
              <DesktopTabLabel
                key={tab.label}
                isActive={activeTab === tab.label}
                onClick={() => handleTabClick(index)}
                tab={tab}
              />
            ))}
          </div>

          <div className="relative min-h-[400px]">
            {data.map((tab) => (
              <DesktopTabContent
                key={tab.label}
                isActive={activeTab === tab.label}
                tab={tab}
              />
            ))}
          </div>
        </div>

        <div className="relative flex h-[624px] w-[560px] flex-col gap-y-1.5">
          {data.map((tab) => (
            <DesktopTabImage
              key={tab.label}
              isActive={activeTab === tab.label}
              tab={tab}
            />
          ))}
        </div>
      </div>
    </>
  );
};
